import { useState, useRef, useEffect } from 'react';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '../hooks';
import { formattedDate } from '@/shared/utils/formatDate';

const NotificationPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useNotifications(1, 20); // Fetch first 20 notifications
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = data?.notifications || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  const handleNotificationClick = (id: string, isRead: boolean) => {
    if (!isRead) {
      markAsRead.mutate(id);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition"
      >
        <div className="cursor-pointer w-8 h-8">
          <img
            src="/assets/icons/bell.svg"
            alt="Notification Icon"
            className="w-full h-full"
          />
        </div>

        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className="absolute right-0 mt-2 w-100 shadow-3xl rounded-3xl z-50 animate-fade-in p-6 bg-white"
          style={{
            boxShadow: '0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A',
          }}
        >
          <div className="flex items-center justify-between py-2 mb-4">
            <h4 className="text-2xl font-bold text-text-primary">
              Notifications
            </h4>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                disabled={markAllAsRead.isPending}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-120 overflow-y-auto scrollbar-hide">
            {isLoading ? (
              <div className="p-4 text-center text-text-secondary">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-text-secondary">
                No notifications
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n.id, n.isRead)}
                  className={`p-3 rounded-lg mb-2 transition relative cursor-pointer ${
                    !n.isRead
                      ? 'bg-blue-50 hover:bg-blue-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p
                      className={`font-bold text-lg text-text-primary ${
                        !n.isRead ? 'text-blue-900' : ''
                      }`}
                    >
                      {n.title}
                    </p>
                    {!n.isRead && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-2 mb-1">
                    {n.message}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formattedDate(n.createdAt)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
